import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Send, Bot, User, X, Sparkles, minus, Download } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend, LabelList, LineChart, Line
} from 'recharts';
import html2pdf from 'html2pdf.js';
import * as XLSX from 'xlsx';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRange } from 'react-date-range';
import { format } from 'date-fns';
import Lottie from 'lottie-react';
import './Chatbot.css';
import useAuth from '../../Hooks/useAuth';
import logoIcon from "../../assets/Preload/naf-logo-loading.gif"
// import chatbotAnimation from "../../assets/Pillpal-chat-icon_doctor.jpeg"
import chatbotAnimation from "../../assets/Preload/animations/chatbotIcon.json"

import { useTranslation } from 'react-i18next';
import { CHATBOT_WEBHOOK_URL, STORAGE_KEYS } from '../../Utils/constants';
import { getUserDetails } from '../../Services/authService';
// import pillpallogo from "../../assets/pillapl-logo.svg";

const N8N_WEBHOOK_URL = 'https://n8n.naf-cloudsystem.de/webhook/chatbot-completedd';
const N8N_API_URL = 'https://n8n.naf-cloudsystem.de';
const N8N_API_KEY = process.env.REACT_APP_N8N_API_KEY || ''; // Add to .env file
const FETCH_TIMEOUT_MS = 300000; // 5 minutes timeout for backend requests

// Stop n8n execution via API
const stopN8NExecution = async (executionId) => {
  if (!executionId) {
    console.warn('No execution ID provided');
    return;
  }

  try {
    const response = await fetch(`${N8N_API_URL}/rest/executions/${executionId}/stop`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${N8N_API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      console.error('Failed to stop execution:', response.status, response.statusText);
      return false;
    }

    // console.log('✅ Execution stopped successfully:', executionId);
    return true;
  } catch (error) {
    console.error('Error stopping execution:', error);
    return false;
  }
};

const fetchWithTimeout = async (url, options = {}, timeoutMs = FETCH_TIMEOUT_MS, t, abortController = null) => {
  const tr = (k, f) => t ? t(k, f) : f;

  const controller = abortController || new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const fetchOptions = { ...options };
  if (abortController) {
    fetchOptions.signal = controller.signal;
  }

  try {
    const res = await fetch(url, fetchOptions);
    clearTimeout(timer);
    return res;
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') {
      throw new Error(tr('chatbot.error_cancelled', 'Request cancelled by user.'));
    }
    throw new Error(
      err.message === 'Failed to fetch'
        ? tr('chatbot.error_high_traffic', 'Could not reach the server. This may be a network issue or the server may be down. Please try again in a moment.')
        : (err.message || tr('chatbot.error_unknown', 'Unknown network error occurred.'))
    );
  }
};

export const ChatInterface = ({
  messages, setMessages, isTyping, setIsTyping, isOpen, onClose, onOpen
}) => {
  const { user } = useAuth();
  const { t, i18n, ready } = useTranslation(); // Get i18n, t function, and ready flag for current language
  const [input, setInput] = useState('');
  const [currentTopic, setCurrentTopic] = useState(null);
  const [sessionId] = useState(() => {
    let id = localStorage.getItem('chatSessionId');
    if (!id) {
      id = 'session-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('chatSessionId', id);
    }
    return id;
  });
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const messagesContainerRef = useRef(null); // Ref for messages container
  const lastBotMessageRef = useRef(null); // Ref for the last bot message element (scroll target)
  const hasUserSentMessage = useRef(false); // Track if user has sent first message
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  const abortControllerRef = useRef(null); // Ref to track current abort controller
  const executionIdRef = useRef(null); // Ref to track current n8n execution ID
  const [isStopping, setIsStopping] = useState(false); // Track stopping state
  const isTypingRef = useRef(false); // Ref to track typing state (avoids stale closure)
  const [isDragging, setIsDragging] = useState(false);
  const [chatPosition, setChatPosition] = useState({ right: 20, bottom: 20 });
  const [isPositionResetting, setIsPositionResetting] = useState(false); // Track position reset animation
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false); // Track scroll button visibility

  // ─── Translation Centralization ───
  const menuTranslationKeys = useMemo(() => [
    'chatbot.menu_home', 'chatbot.menu_view', 'chatbot.menu_sales_analytics',
    'chatbot.menu_transactions', 'chatbot.menu_reports', 'chatbot.menu_manage',
    'chatbot.menu_vending_machines', 'chatbot.menu_inventory', 'chatbot.menu_advertisements',
    'chatbot.menu_customers', 'chatbot.menu_discounts', 'chatbot.menu_coupons',
    'chatbot.menu_users', 'chatbot.menu_taxes', 'chatbot.menu_maintenance', 'chatbot.menu_spare_parts',
    'chatbot.menu_service_requests', 'chatbot.menu_franchise', 'chatbot.menu_other'
  ], []);

  const textTranslationKeys = useMemo(() => [
    'chatbot.welcome_message', 'chatbot.click_option'
  ], []);

  // Build reverse maps: any-language value → translation key
  const reverseMenuMap = useMemo(() => {
    const map = {};
    ['en', 'de', 'fr', 'es', 'it', 'ch'].forEach(lang => {
      const fixedT = i18n.getFixedT(lang);
      menuTranslationKeys.forEach(key => {
        const translated = fixedT(key);
        if (translated && translated !== key) map[translated] = key;
      });
    });
    return map;
  }, [i18n.language, menuTranslationKeys, ready]);

  const reverseTextMap = useMemo(() => {
    const map = {};
    ['en', 'de', 'fr', 'es', 'it', 'ch'].forEach(lang => {
      const fixedT = i18n.getFixedT(lang);
      textTranslationKeys.forEach(key => {
        const translated = fixedT(key);
        if (translated && translated !== key) map[translated] = key;
      });
    });
    return map;
  }, [i18n.language, textTranslationKeys, ready]);

  const translateLabel = useCallback((name, type = 'menu') => {
    const map = type === 'menu' ? reverseMenuMap : reverseTextMap;
    if (map[name]) return t(map[name]);
    return name;
  }, [reverseMenuMap, reverseTextMap, t]);

  // Ref for messages container
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Date Picker State ───
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [activePickerId, setActivePickerId] = useState(null);
  const [taxYear, setTaxYear] = useState(2025);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  // Sync isTyping state with ref
  useEffect(() => {
    isTypingRef.current = isTyping;
  }, [isTyping]);

  /**
   * Handle the start of a drag operation. Captures initial pointer offset.
   */
  const handleDragStart = useCallback((e) => {
    // Only respond to left mouse button or touch
    if (e.type === 'mousedown' && e.button !== 0) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const container = chatContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    // Offset = distance from the pointer to the TOP-LEFT of the element
    dragOffsetRef.current = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };

    isDraggingRef.current = true;
    setIsDragging(true);

    // Prevent text selection while dragging
    e.preventDefault();
  }, []);

  /**
   * Handle drag movement. Converts pointer position to bottom/right CSS values
   * while clamping to the viewport so the window can't be dragged off-screen.
   */
  const handleDragMove = useCallback((e) => {
    if (!isDraggingRef.current) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const container = chatContainerRef.current;
    if (!container) return;

    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Calculate where the top-left of the element should be
    let newLeft = clientX - dragOffsetRef.current.x;
    let newTop = clientY - dragOffsetRef.current.y;

    // Clamp so the entire window stays within the viewport
    newLeft = Math.max(0, Math.min(newLeft, viewportW - containerWidth));
    newTop = Math.max(0, Math.min(newTop, viewportH - containerHeight));

    // Convert left/top to right/bottom for CSS positioning
    const newRight = viewportW - newLeft - containerWidth;
    const newBottom = viewportH - newTop - containerHeight;

    setChatPosition({ right: newRight, bottom: newBottom });
  }, []);

  /**
   * End drag: cleanup state.
   */
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  // Attach global mouse/touch move and end listeners while dragging.
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Re-clamp position when the browser is resized.
  useEffect(() => {
    const handleResize = () => {
      setChatPosition((prev) => {
        const container = chatContainerRef.current;
        if (!container) return prev;
        const cW = container.offsetWidth;
        const cH = container.offsetHeight;
        const vW = window.innerWidth;
        const vH = window.innerHeight;
        return {
          right: Math.max(0, Math.min(prev.right, vW - cW)),
          bottom: Math.max(0, Math.min(prev.bottom, vH - cH))
        };
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ─── Scroll detection for scroll-to-bottom button ───
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const threshold = clientHeight * 0.2; // 20% of container height

      // Show button if scrolled up more than 20% from bottom
      setShowScrollButton(distanceFromBottom > threshold);
    };

    messagesContainer.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    return () => {
      messagesContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    // Hide the button after scrolling
    setTimeout(() => setShowScrollButton(false), 300);
  };

  // Scroll to top of messages container
  const scrollToTop = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      // Hide the button after scrolling
      setTimeout(() => setShowScrollButton(false), 300);
    }
  };

  // Check if message content is large (over threshold)
  const isLargeMessage = (message) => {
    if (!message || message.role !== 'assistant') return false;

    const textContent = message.data?.text || message.data?.error || '';
    const structuredContent = message.data?.structured;

    // Check text length
    if (textContent.length > 800) return true;

    // Check if structured response has lots of content
    if (structuredContent) {
      const layoutItems = structuredContent.layout || [];
      if (layoutItems.length > 3) return true;

      // Check text items in layout
      for (const item of layoutItems) {
        if (item.type === 'text' && item.content && item.content.length > 600) {
          return true;
        }
      }
    }

    return false;
  };

  // Scroll to the newly rendered bot message so its top aligns with the viewport top.
  // Uses scrollIntoView({ block: 'start', behavior: 'smooth' }) to keep container
  // height and content layout intact — nothing is hidden, collapsed, or altered.
  useEffect(() => {
    if (hasUserSentMessage.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage.role === 'assistant') {
        // Small delay to ensure the DOM has fully rendered the new message
        // (handles rapid message generation and complex content like charts/tables)
        const scrollTimer = setTimeout(() => {
          if (lastBotMessageRef.current) {
            lastBotMessageRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 150);

        // Hide scroll button after auto-scroll
        setShowScrollButton(false);

        return () => clearTimeout(scrollTimer);
      } else {
        // For user messages, scroll to the bottom so they see their own message
        scrollToBottom();
        setShowScrollButton(false);
      }
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Elapsed time counter while typing ───
  useEffect(() => {
    if (isTyping) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedTime(0);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTyping]);

  // ─── Get friendly wait message based on elapsed time ───
  const getWaitMessage = (seconds) => {
    if (seconds < 3) return null; // Don't show message for very fast responses
    if (seconds < 8) return t('chatbot.wait_short', '⏳ Please wait...');
    if (seconds < 15) return t('chatbot.wait_medium', '⏳ Working on it, please hold on...');
    // if (seconds < 30) return t('chatbot.wait_report', '📊 Generating your report, this may take a moment...');
    // if (seconds < 60) return t('chatbot.wait_large', '📊 Still working on it — large reports take a bit longer...');
    if (seconds < 60) return t('chatbot.wait_crunching', '⚡ Crunching numbers... hang tight!');
    if (seconds < 120) return t('chatbot.wait_processing', '⏳ This is a large request — still processing...');
    if (seconds < 180) return t('chatbot.wait_complex', '📊 Over 2 minutes now — complex reports can take up to 5 minutes...');
    if (seconds < 240) return t('chatbot.wait_almost', '⚡ Still working hard on your data — almost there...');
    if (seconds < 290) return t('chatbot.wait_finalizing', '✨ Finalizing your report — thank you for your patience!');
    return t('chatbot.wait_long', '⏳ This is taking longer than expected — please wait a moment more...');
  };


  // ─── Fetch User Details on Mount ───
  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await getUserDetails();
      setEmployeeData(response);
      // console.log('✅ Employee data loaded:', response);
    } catch (error) {
      console.error('❌ Failed to fetch user:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── User Context Data ───
  const getUserData = () => {
    const rawAuthorities = employeeData?.authorities || [];

    // Spring Boot returns: [{authority: 'ROLE_ADMIN'}]
    // Some endpoints return plain strings: ['ROLE_ADMIN']
    // Handle both:
    const authorityStrings = rawAuthorities.map(a =>
      typeof a === 'string' ? a : (a?.authority || a?.name || a?.role || '')
    ).filter(Boolean);

    // Pick the highest-priority role so ADMIN is never downgraded to EMPLOYEE
    const rolePriority = ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_EMPLOYEE'];
    const bestAuthority = rolePriority.find(r => authorityStrings.includes(r)) || authorityStrings[0] || null;
    const userRole = bestAuthority ? bestAuthority.replace('ROLE_', '').toLowerCase() : null;

    const userName = employeeData?.firstName && employeeData?.lastName
      ? `${employeeData.firstName} ${employeeData.lastName}`
      : (employeeData?.login || null);

    // franchiseId field name can vary by API version
    const franchiseId = employeeData?.franchiseId ?? employeeData?.franchise_id ?? employeeData?.franchise?.id ?? null;

    const userData = {
      userRole,
      userAuthority: bestAuthority,
      authorities: authorityStrings,
      userName,
      userEmail: employeeData?.email || null,
      userLogin: employeeData?.login || null,
      franchiseId,
      language: i18n?.language || 'de',
      userAgent: navigator.userAgent,
      hasUserProfile: Boolean(employeeData)
    };

    // console.log('👤 User Data:', userData);
    return userData;
  };

  // ─── Get webhook headers with authentication ───
  const getWebhookHeaders = () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
    return headers;
  };

  // ─── Go to Main Menu ───
  const goToMainMenu = useCallback(async () => {
    setCurrentTopic(null);
    setInput('');
    setIsTyping(true);
    try {
      const userData = getUserData();
      const response = await fetchWithTimeout(CHATBOT_WEBHOOK_URL, {
        method: 'POST',
        headers: getWebhookHeaders(),
        body: JSON.stringify({
          message: 'hi',
          sessionId: sessionId,
          ...userData
        })
      }, FETCH_TIMEOUT_MS, t, abortControllerRef.current);

      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      if (data.output) {
        const structured = parseWorkflowOutput(data.output);
        if (structured) {
          data.structured = structured;
        } else {
          data.text = extractTextFromOutput(data.output);
        }
      }
      addBotMessage(data);
    } catch (error) {
      console.error('Go to main menu error:', error);
    } finally {
      setIsTyping(false);
    }
  }, [setMessages, sessionId, getUserData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Reset Chat ───
  const handleReset = useCallback(() => {
    setMessages([]);
    setCurrentTopic(null);
    setInput('');
    // Generate a new session ID for a fresh start
    const newSessionId = 'session-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('chatSessionId', newSessionId);

    // After clearing, trigger the initial menu
    setTimeout(() => {
      goToMainMenu();
    }, 100);
  }, [setMessages, goToMainMenu]);

  // Auto-send greeting on first open to load the menu immediately
  const hasAutoGreeted = useRef(false);
  useEffect(() => {
    if (isOpen && !hasAutoGreeted.current && messages.length === 0) {
      hasAutoGreeted.current = true;
      // Silently fetch menu without showing a user "hi" bubble
      const fetchMenu = async () => {
        setIsTyping(true);
        try {
          const userData = getUserData();
          const response = await fetchWithTimeout(CHATBOT_WEBHOOK_URL, {
            method: 'POST',
            headers: getWebhookHeaders(),
            body: JSON.stringify({
              message: 'hi',
              sessionId: sessionId,
              ...userData
            })
          }, FETCH_TIMEOUT_MS, t);
          if (!response.ok) throw new Error(`Server error: ${response.status}`);
          const data = await response.json();
          if (data.output) {
            const structured = parseWorkflowOutput(data.output);
            if (structured) {
              data.structured = structured;
            } else {
              data.text = data.output;
            }
          }
          addBotMessage(data);
        } catch (error) {
          // Silently handle auto-greeting errors
        } finally {
          setIsTyping(false);
        }
      };
      fetchMenu();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps


  // Parse bot text: convert \n to line breaks and markdown links to clickable links
  const parseBotText = (text) => {
    if (!text) return '';

    let formatted = text;

    // Handle escaped newlines first
    formatted = formatted.replace(/\\r\\n/g, '\n');
    formatted = formatted.replace(/\\n/g, '\n');

    // Split into paragraphs/blocks by double newlines
    const blocks = formatted.split(/\n\n+/);

    const processedBlocks = blocks.map(block => {
      // Skip empty blocks
      if (!block.trim()) return '';

      const lines = block.split('\n').filter(line => line.trim());

      // Check if this block is a numbered list
      const isNumberedList = lines.length > 0 && /^\d+[.\)]\s/.test(lines[0].trim());

      // Check if this block is a bullet list
      const isBulletList = lines.length > 0 && /^[\-\*•]\s/.test(lines[0].trim());

      // NEW: Check if this block looks like sequential instructions/steps
      const isSequentialSteps = detectSequentialSteps(lines);

      // Debug logging
      if (lines.length >= 3) {
        // console.log(' Block analysis:', {
        //   lineCount: lines.length,
        //   firstLine: lines[0].substring(0, 50),
        //   isNumberedList,
        //   isBulletList,
        //   isSequentialSteps,
        //   lines: lines.slice(0, 3).map(l => l.trim().substring(0, 40))
        // });
      }

      if (isNumberedList) {
        // Process numbered list
        const items = lines.map(line => {
          const trimmed = line.trim();
          const content = trimmed.replace(/^\d+[.\)]\s*/, '');
          return `<li style=" list-style-type: decimal;">${processInlineFormatting(content)}</li>`;
        }).join('');
        return `<ol class="chat-numbered-list">${items}</ol>`;
      } else if (isBulletList) {
        // Process bullet list
        const items = lines.map(line => {
          const trimmed = line.trim();
          const content = trimmed.replace(/^[\-\*•]\s*/, '');
          return `<li style=" list-style-type: disc;">${processInlineFormatting(content)}</li>`;
        }).join('');
        return `<ul class="chat-bullet-list">${items}</ul>`;
      } else if (isSequentialSteps) {
        // Convert sequential instructions to numbered list
        const items = lines.map(line => {
          const trimmed = line.trim();
          return `<li style=" list-style-type: disc;">${processInlineFormatting(trimmed)}</li>`;
        }).join('');
        return `<ol class="chat-numbered-list">${items}</ol>`;
      } else {
        // Regular paragraph - check for section headers (text followed by colon)
        if (lines.length === 1 && lines[0].trim().endsWith(':') && lines[0].trim().length < 50) {
          return `<div class="chat-section-header">${processInlineFormatting(lines[0].trim())}</div>`;
        }

        // Regular paragraph with multiple lines
        const processedLines = lines.map(line => processInlineFormatting(line.trim()));
        return `<p class="chat-paragraph">${processedLines.join('<br>')}</p>`;
      }
    });

    return processedBlocks.filter(block => block).join('');
  };

  // Detect if lines look like sequential steps/instructions
  const detectSequentialSteps = (lines) => {
    if (lines.length < 2) return false;

    // Common patterns that indicate sequential steps:
    const stepPatterns = [
      /^go\s+to/i,              // "Go to..."
      /^select\s+/i,            // "Select..."
      /^click\s+/i,             // "Click..."
      /^find\s+/i,              // "Find..."
      /^open\s+/i,              // "Open..."
      /^navigate\s+/i,          // "Navigate..."
      /^enter\s+/i,             // "Enter..."
      /^type\s+/i,              // "Type..."
      /^choose\s+/i,            // "Choose..."
      /^pick\s+/i,              // "Pick..."
      /^press\s+/i,             // "Press..."
      /^tap\s+/i,               // "Tap..."
      /^confirm\s+/i,           // "Confirm..."
      /^verify\s+/i,            // "Verify..."
      /^check\s+/i,             // "Check..."
      /^ensure\s+/i,            // "Ensure..."
      /^make\s+sure/i,          // "Make sure..."
    ];

    // Count how many lines match step patterns
    const stepMatches = lines.filter(line => {
      const trimmed = line.trim();
      return stepPatterns.some(pattern => pattern.test(trimmed));
    });

    // If 50% or more lines match step patterns, treat as sequential steps
    return stepMatches.length >= Math.ceil(lines.length * 0.5);
  };

  // Process inline formatting (bold, links, etc.)
  const processInlineFormatting = (text) => {
    if (!text) return '';

    let formatted = text;

    // Bold text: **text** or __text__
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    formatted = formatted.replace(/__([^_]+)__/g, '<strong>$1</strong>');

    // Italic text: *text* or _text_
    formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    formatted = formatted.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

    // Links: [text](url)
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Inline code: `code`
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="chat-inline-code">$1</code>');

    return formatted;
  };

  // Parse the structured workflow output (may be stringified JSON, possibly wrapped in markdown code blocks)
  const parseWorkflowOutput = (output) => {
    if (!output) return null;
    let parsed = output;
    // If output is a string, try to parse it as JSON
    if (typeof output === 'string') {
      try {
        // Strip markdown code block wrappers (```json ... ```)
        let cleanStr = output.replace(/```json/gi, '').replace(/```/g, '').trim();
        // Try to extract JSON object from first { to last }
        const firstBrace = cleanStr.indexOf('{');
        const lastBrace = cleanStr.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          cleanStr = cleanStr.substring(firstBrace, lastBrace + 1);
        }
        parsed = JSON.parse(cleanStr);
      } catch (e) {
        // Not JSON, return as plain text
        return null;
      }
    }
    // Check if it looks like a structured response (has layout array or summary)
    if (parsed && typeof parsed === 'object' && (parsed.layout || parsed.summary)) {
      // Auto-convert report format (report:true + columns + rows) into layout array
      if (parsed.report && parsed.columns && parsed.rows && !parsed.layout) {
        parsed.layout = [{
          type: 'table',
          title: parsed.title || 'Report',
          columns: parsed.columns,
          rows: parsed.rows
        }];
      }
      return parsed;
    }
    // Normalize flat PDF responses (e.g. { text, pdf_url, fileName })
    if (parsed && typeof parsed === 'object' && (parsed.pdf_url || parsed.pdfUrl)) {
      const pdfDataUri = parsed.pdf_url || parsed.pdfUrl;
      return {
        summary: parsed.text || '📄 Your report is ready!',
        layout: [{
          type: 'pdf_download',
          title: parsed.title || 'PDF Report',
          data: pdfDataUri,
          filename: parsed.fileName || parsed.filename || 'report.pdf'
        }]
      };
    }
    return null;
  };

  // Extract plain text from output that may be a string, JSON string, or object
  const extractTextFromOutput = (output) => {
    if (!output) return '';
    // If it's a string, try to parse as JSON and extract a text field
    if (typeof output === 'string') {
      try {
        const parsed = JSON.parse(output.replace(/```json/gi, '').replace(/```/g, '').trim());
        if (parsed && typeof parsed === 'object') {
          return parsed.text || parsed.message || parsed.output || JSON.stringify(parsed);
        }
        return String(parsed);
      } catch {
        return output;
      }
    }
    // If it's an object, extract common text fields
    if (typeof output === 'object') {
      return output.text || output.message || output.output || JSON.stringify(output);
    }
    return String(output);
  };

  // Add a bot message with structured data
  const addBotMessage = useCallback((data) => {
    const botMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      data: data, // Structured data from n8n
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMessage]);
  }, [setMessages]);

  // Add an error message
  const addErrorMessage = useCallback((errorText) => {
    const errorMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      data: { error: errorText },
      timestamp: new Date()
    };
    setMessages(prev => [...prev, errorMessage]);
  }, [setMessages]);

  // Show user-friendly error for high traffic or system issues
  const showHighTrafficError = useCallback(() => {
    const friendlyMessage = t(
      'chatbot.error_high_traffic',
      "🌟 Our system is currently experiencing high traffic. Please try again in a few moments. Thank you for your patience!"
    );
    addErrorMessage(friendlyMessage);
  }, [t, addErrorMessage]);

  // Add a user message
  const addUserMessage = useCallback((text) => {
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    hasUserSentMessage.current = true; // Mark that user has sent their first message
  }, [setMessages]);

  // ─── Cancel current request ───
  const cancelRequest = useCallback(async () => {
    // console.log('🛑 Cancel button clicked');
    setIsStopping(true);

    // Stop n8n execution first
    if (executionIdRef.current) {
      // console.log('🛑 Stopping n8n execution:', executionIdRef.current);
      await stopN8NExecution(executionIdRef.current);
      executionIdRef.current = null;
    }

    // Abort frontend fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset all state
    setIsStopping(false);
    setIsTyping(false);
    isTypingRef.current = false; // Also reset the ref
    setElapsedTime(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // console.log('✅ Request cancelled, UI state reset');
  }, [setIsTyping]);

  // ─── API: Send a regular chat message ───
  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || isTypingRef.current) {
      // console.log('⚠️ Request already in progress or empty message');
      return; // Block if already waiting for a response
    }

    addUserMessage(text);
    if (!messageText) setInput('');
    setIsTyping(true);

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const userData = getUserData();
      const payload = {
        message: text,
        currentTopic: currentTopic,
        sessionId: sessionId,
        ...userData
      };
      // console.log('💬 Chatbot: Sending message:', payload);

      const response = await fetchWithTimeout(CHATBOT_WEBHOOK_URL, {
        method: 'POST',
        headers: getWebhookHeaders(),
        body: JSON.stringify(payload)
      }, FETCH_TIMEOUT_MS, t, abortControllerRef.current);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`❌ Chatbot: Webhook error (${response.status}):`, errorBody);
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      // console.log('✅ Chatbot: Received response:', data);

      // Capture execution ID if returned by n8n webhook
      if (data.executionId || data.execution_id) {
        executionIdRef.current = data.executionId || data.execution_id;
        // console.log('📝 Captured execution ID:', executionIdRef.current);
      }

      // Handle the output field mapping (AI Agent sends answer in 'output')
      if (data.output) {
        const structured = parseWorkflowOutput(data.output);
        if (structured) {
          data.structured = structured;
        } else {
          data.text = extractTextFromOutput(data.output);
        }
      }

      // Mark whether this request was typed by the user or clicked via suggestion
      data.isTyped = !messageText;

      addBotMessage(data);
    } catch (error) {
      console.error('Error:', error);
      // Don't show error message if request was cancelled by user
      if (error.message !== t('chatbot.error_cancelled', 'Request cancelled by user.')) {
        showHighTrafficError();
      }
    } finally {
      abortControllerRef.current = null;
      executionIdRef.current = null; // Clear execution ID
      // console.log('🔄 sendMessage finally: resetting isTyping');
      setIsTyping(false);
    }
  };

  // ─── API: Ask about a topic (menu item click) ───
  const askAboutTopic = async (topic) => {
    if (isTypingRef.current) {
      // console.log('⚠️ Request already in progress, ignoring click');
      return; // Block if already waiting for a response
    }

    // Display the localized label in the chat bubble, but send the technical (English) name to n8n
    const localizedLabel = translateLabel(topic, 'menu');
    addUserMessage(localizedLabel);
    setCurrentTopic(topic);

    // ─── Local Shortcuts: Trigger Date Picker immediately for these menu items ───
    const topicPickerMap = {
      'Custom Range': 'datepicker:custom'
    };

    if (topicPickerMap[topic]) {
      setActivePickerId(topicPickerMap[topic]);
      setIsDatePickerOpen(true);
      return; // Skip n8n call, let the date picker handle it
    }

    setIsTyping(true);

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const userData = getUserData();
      const payload = {
        selectedMenu: topic,
        sessionId: sessionId,
        ...userData
      };

      const response = await fetchWithTimeout(CHATBOT_WEBHOOK_URL, {
        method: 'POST',
        headers: getWebhookHeaders(),
        body: JSON.stringify(payload)
      }, FETCH_TIMEOUT_MS, t, abortControllerRef.current);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`❌ Chatbot: Webhook error (${response.status}):`, errorBody);
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();

      // Capture execution ID if returned by n8n webhook
      if (data.executionId || data.execution_id) {
        executionIdRef.current = data.executionId || data.execution_id;
      }

      // Handle output field mapping
      if (data.output) {
        const structured = parseWorkflowOutput(data.output);
        if (structured) {
          data.structured = structured;
        } else {
          data.text = extractTextFromOutput(data.output);
        }
      }

      addBotMessage(data);
    } catch (error) {
      console.error('Error:', error);
      // Don't show error message if request was cancelled by user
      if (error.message !== t('chatbot.error_cancelled', 'Request cancelled by user.')) {
        showHighTrafficError();
      }
    } finally {
      abortControllerRef.current = null;
      executionIdRef.current = null; // Clear execution ID
      setIsTyping(false);
    }
  };

  // ─── API: Select an intent (question button click) ───
  const selectIntent = async (intentId, questionText) => {
    if (isTypingRef.current) {
      return; // Block if already waiting for a response
    }

    if (intentId.startsWith('datepicker:')) {
      setActivePickerId(intentId);
      setIsDatePickerOpen(true);
      return; // Stop here, wait for user to pick date
    }

    addUserMessage(questionText);
    setIsTyping(true);

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const userData = getUserData();
      const payload = {
        selectedIntent: intentId,
        currentTopic: currentTopic,
        sessionId: sessionId,
        ...userData
      };

      const response = await fetchWithTimeout(CHATBOT_WEBHOOK_URL, {
        method: 'POST',
        headers: getWebhookHeaders(),
        body: JSON.stringify(payload)
      }, FETCH_TIMEOUT_MS, t, abortControllerRef.current);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`❌ Chatbot: Webhook error (${response.status}):`, errorBody);
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();

      // Capture execution ID if returned by n8n webhook
      if (data.executionId || data.execution_id) {
        executionIdRef.current = data.executionId || data.execution_id;
        // console.log('📝 Captured execution ID:', executionIdRef.current);
      }

      // Handle output field mapping
      if (data.output) {
        const structured = parseWorkflowOutput(data.output);
        if (structured) {
          data.structured = structured;
        } else {
          data.text = extractTextFromOutput(data.output);
        }
      }

      addBotMessage(data);
    } catch (error) {
      console.error('Error:', error);
      // Don't show error message if request was cancelled by user
      if (error.message !== t('chatbot.error_cancelled', 'Request cancelled by user.')) {
        showHighTrafficError();
      }
    } finally {
      abortControllerRef.current = null;
      executionIdRef.current = null; // Clear execution ID
      setIsTyping(false);
    }
  };

  // ─── Handler for submitting custom date selection ───
  const handleDateSelectionSubmit = async () => {
    const startDate = dateRange[0].startDate;
    const endDate = dateRange[0].endDate;
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');

    // Logic to check if it's a "Full Month" selection
    const isFirstDay = startDate.getDate() === 1;
    const isLastDay = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0).getDate() === endDate.getDate();
    const isSameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
    const type = activePickerId.split(':')[1];

    let displayMessage = `📅 ${start}`;
    if (start !== end) {
      if (isFirstDay && isLastDay && isSameMonth) {
        // It's a full month! e.g., "March 2024 Report"
        if (type === 'tax') {
          displayMessage = `📅 Tax report of ${format(startDate, 'MMMM yyyy')} (${start} to ${end})`;
        } else {
          displayMessage = `📅 ${format(startDate, 'MMMM yyyy')} Report (${start} to ${end})`;
        }
      } else if (type === 'weekly') {
        displayMessage = `📅 Weekly Report (${start} to ${end})`;
      } else if (type === 'transactions') {
        displayMessage = `📅 Transactions (${start} to ${end})`;
      } else if (type === 'custom') {
        displayMessage = `📅 Custom Report (${start} to ${end})`;
      } else {
        displayMessage += ` to ${end}`;
      }
    } else {
      // Single date
      if (type === 'today') {
        displayMessage = `📅 Daily Report (${start})`;
      } else {
        displayMessage = `📅 Report for ${start}`;
      }
    }

    setIsDatePickerOpen(false);
    addUserMessage(displayMessage);
    setIsTyping(true);

    abortControllerRef.current = new AbortController();

    try {
      const userData = getUserData();
      const payload = {
        action: 'custom_report',
        message: displayMessage,
        timeframe: type,
        startDate: start,
        endDate: end,
        sessionId: sessionId,
        ...userData
      };
      // console.log('📊 Chatbot: Sending report request:', payload);

      const response = await fetchWithTimeout(CHATBOT_WEBHOOK_URL, {
        method: 'POST',
        headers: getWebhookHeaders(),
        body: JSON.stringify(payload)
      }, FETCH_TIMEOUT_MS, t, abortControllerRef.current);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`❌ Chatbot: Webhook error (${response.status}):`, errorBody);
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      // console.log('✅ Chatbot: Report response:', data);

      if (data.output) {
        const structured = parseWorkflowOutput(data.output);
        if (structured) {
          data.structured = structured;
        } else {
          data.text = extractTextFromOutput(data.output);
        }
      }

      addBotMessage(data);
    } catch (error) {
      console.error('Error:', error);
      if (error.message !== t('chatbot.error_cancelled', 'Request cancelled by user.')) {
        showHighTrafficError();
      }
    } finally {
      setIsTyping(false);
      abortControllerRef.current = null;
      setActivePickerId(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Create blob URL from base64 data (supports raw base64 or data: URI) ───
  const createBlobUrl = (base64Input, mimeType = 'application/pdf') => {
    if (!base64Input || typeof base64Input !== 'string') return null;

    // If it's already a URL, just return it
    if (base64Input.startsWith('http')) return base64Input;

    try {
      let base64Data = base64Input;
      let contentType = mimeType;

      // If it's a data: URI, extract the parts
      if (base64Input.startsWith('data:')) {
        const parts = base64Input.split(',');
        if (parts.length > 1) {
          base64Data = parts[1];
          // Try to extract mime type if present
          const mimeMatch = parts[0].match(/:(.*?);/);
          if (mimeMatch) contentType = mimeMatch[1];
        }
      }

      // Sanitize: Remove all whitespace, newlines, and ANY non-base64 characters
      // This is the most common cause of atob() failure with API data.
      base64Data = base64Data.replace(/[^A-Za-z0-9+/=]/g, '');

      // Check if padding is missing (length should be multiple of 4)
      while (base64Data.length % 4 !== 0) {
        base64Data += '=';
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error('Error creating blob from base64:', e);
      return null;
    }
  };

  // ─── Premium color palette ───
  const CHART_COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#00f2fe', '#43e97b', '#fa709a', '#fee140'];

  // ─── Custom Tooltip for Charts ───
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-custom-tooltip">
          <div className="tooltip-label">{label}</div>
          <div className="tooltip-value">
            {typeof payload[0].value === 'number'
              ? payload[0].value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : payload[0].value}
          </div>
        </div>
      );
    }
    return null;
  };

  // ─── Download chart as PNG helper ───
  const downloadChartAsPng = (containerEl, title) => {
    if (!containerEl) return;
    const svgEl = containerEl.querySelector('.recharts-wrapper svg');
    if (!svgEl) return;
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      ctx.scale(2, 2);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `${title || 'chart'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // ─── Chart card wrapper ───
  const ChartCard = ({ title, children }) => {
    let chartBodyRef = null;
    return (
      <div className="layout-chart-card">
        <div className="chart-card-header">
          <div className="chart-title">{title}</div>
          <button
            className="chart-download-btn"
            onClick={() => downloadChartAsPng(chartBodyRef, title)}
            title="Download as image"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        </div>
        <div className="chart-body" ref={el => { chartBodyRef = el; }}>
          {children}
        </div>
      </div>
    );
  };

  // ─── Convert HTML string to PDF and trigger download (client-side) ───
  const convertHtmlToPdf = async (htmlContent, filename, setStatus) => {
    try {
      setStatus('generating');
      // Create a hidden iframe to hold the exact, untouched HTML string
      // This prevents React/browser from stripping <head>, <style>, or <body> tags
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.left = '-9999px';
      iframe.style.top = '0';
      iframe.style.width = '1024px'; // typical desktop width to prevent mobile squishing
      iframe.style.height = '1448px'; // A4 aspect ratio approximation
      iframe.style.zIndex = '-1';
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      // Write the exact HTML we got from the backend without any changes
      doc.write(htmlContent);
      doc.close();

      // Wait a moment for fonts or images inside the iframe to render
      await new Promise(resolve => setTimeout(resolve, 500));

      const opt = {
        margin: [5, 5, 10, 5], // top, left, bottom, right in mm
        filename: filename,
        image: { type: 'jpeg', quality: 1.0 }, // Maximum quality JPEG to keep file size low
        html2canvas: {
          scale: 2, // Reverted to 2 to prevent 60MB+ file sizes
          useCORS: true,
          letterRendering: true,
          logging: false,
          windowWidth: 1024
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Convert the entire HTML document (including head/styles) to PDF
      await html2pdf().set(opt).from(doc.documentElement).save();

      document.body.removeChild(iframe);
      setStatus('done');
      // Reset status after a short delay
      setTimeout(() => setStatus('ready'), 2000);
    } catch (err) {
      console.error('PDF generation error:', err);
      setStatus('error');
      setTimeout(() => setStatus('ready'), 3000);
    }
  };

  // ─── PDF Download Button with status management ───
  const PdfDownloadCard = ({ htmlContent, base64Data, pdfUrl, filename, title }) => {
    const [pdfStatus, setPdfStatus] = useState('ready');

    const handleDownload = () => {
      if (htmlContent) {
        // Client-side HTML-to-PDF conversion
        convertHtmlToPdf(htmlContent, filename, setPdfStatus);
      } else if (pdfUrl) {
        // Direct PDF URL from n8n
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = filename;
        a.click();
        setPdfStatus('done');
        setTimeout(() => setPdfStatus('ready'), 2000);
      } else if (base64Data) {
        // Legacy: direct base64 PDF download
        const blobUrl = createBlobUrl(base64Data, 'application/pdf');
        if (blobUrl) {
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = filename;
          a.click();
          if (blobUrl.startsWith('blob:')) {
            URL.revokeObjectURL(blobUrl);
          }
          setPdfStatus('done');
          setTimeout(() => setPdfStatus('ready'), 2000);
        }
      }
    };

    const statusConfig = {
      ready: {
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        ),
        label: t('chatbot.download', 'Download'),
        className: 'pdf-download-btn'
      },
      generating: {
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pdf-spinner">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        ),
        label: t('chatbot.generating_pdf', 'Please wait...'),
        className: 'pdf-download-btn pdf-generating'
      },
      done: {
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ),
        label: t('chatbot.pdf_downloaded', 'Downloaded!'),
        className: 'pdf-download-btn pdf-done'
      },
      error: {
        icon: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        ),
        label: t('chatbot.pdf_error', 'Error — Try again'),
        className: 'pdf-download-btn pdf-error'
      }
    };

    const status = statusConfig[pdfStatus];

    return (
      <div className="layout-pdf-download">
        <div className="pdf-card">
          <div className="pdf-icon-area">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div className="pdf-info">
            <div className="pdf-title">{title}</div>
            <div className="pdf-subtitle">{t('chatbot.pdf_subtitle', 'PDF Document • Ready to download')}</div>
          </div>
          <button
            className={status.className}
            onClick={handleDownload}
            disabled={pdfStatus === 'generating'}
          >
            {status.icon}
            {status.label}
          </button>
        </div>
      </div>
    );
  };

  // ─── Render a layout component from structured response ───
  const renderLayoutComponent = (component, idx) => {
    if (!component || !component.type) return null;

    switch (component.type) {
      case 'pdf_download': {
        const base64 = component.data;
        const htmlContent = component.html_content || null;
        const pdfUrl = component.url || null;
        const filename = component.filename || 'report.pdf';
        const title = component.title || 'PDF Report';

        return (
          <PdfDownloadCard
            key={idx}
            htmlContent={htmlContent}
            base64Data={!htmlContent && !pdfUrl ? base64 : null}
            pdfUrl={pdfUrl}
            filename={filename}
            title={title}
          />
        );
      }

      case 'kpi_card': {
        const trendColor = component.trend === 'up' ? '#22c55e' : component.trend === 'down' ? '#ef4444' : '#94a3b8';
        const trendIcon = component.trend === 'up' ? '↑' : component.trend === 'down' ? '↓' : '→';
        return (
          <div key={idx} className="layout-kpi-card">
            <div className="kpi-header">
              <span className="kpi-title">{component.title}</span>
              {component.trend && (
                <span className="kpi-trend-badge" style={{ background: `${trendColor}15`, color: trendColor }}>
                  {trendIcon} {component.trend}
                </span>
              )}
            </div>
            <div className="kpi-value">{component.value}</div>
          </div>
        );
      }

      case 'bar_chart': {
        const chartData = (component.data || []).map(d => ({ name: d.label, value: d.value }));
        return (
          <ChartCard key={idx} title={component.title}>
            <ResponsiveContainer width="100%" height={Math.min(240, 180 + chartData.length * 8)}>
              <BarChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 40 }} barCategoryGap="25%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false}
                  tick={{ fontSize: 9, fill: '#475569', fontWeight: 500 }}
                  interval={0} angle={-45} textAnchor="end" height={60} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} width={35} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="value" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#334155' }} />
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.9} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        );
      }

      case 'horizontal_bar_chart': {
        const chartData = (component.data || []).map(d => ({ name: d.label, value: d.value })).sort((a, b) => b.value - a.value);
        return (
          <ChartCard key={idx} title={component.title}>
            <ResponsiveContainer width="100%" height={Math.max(150, chartData.length * 36)}>
              <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 40, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                  tick={{ fontSize: 10, fill: '#475569', fontWeight: 500 }} width={80} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                  <LabelList dataKey="value" position="right" style={{ fontSize: 10, fontWeight: 600, fill: '#334155' }} />
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.9} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        );
      }

      case 'line_chart': {
        const chartData = (component.data || []).map(d => ({ name: d.label, value: d.value }));
        return (
          <ChartCard key={idx} title={component.title}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#475569' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} width={35} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="value" stroke={component.color || '#667eea'} strokeWidth={2.5}
                  dot={{ r: 4, fill: component.color || '#667eea' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        );
      }

      case 'multi_line_chart': {
        const series = component.series || [];
        const allLabels = [...new Set(series.flatMap(s => (s.data || []).map(d => d.label)))];
        const chartData = allLabels.map(label => {
          const point = { name: label };
          series.forEach(s => {
            const match = (s.data || []).find(d => d.label === label);
            point[s.name] = match ? match.value : 0;
          });
          return point;
        });
        return (
          <ChartCard key={idx} title={component.title}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#475569' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} width={35} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {series.map((s, i) => (
                  <Line key={i} type="monotone" dataKey={s.name} stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2} dot={{ r: 3 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        );
      }

      case 'stacked_bar_chart': {
        const series = component.series || [];
        const allLabels = [...new Set(series.flatMap(s => (s.data || []).map(d => d.label)))];
        const chartData = allLabels.map(label => {
          const point = { name: label };
          series.forEach(s => {
            const match = (s.data || []).find(d => d.label === label);
            point[s.name] = match ? match.value : 0;
          });
          return point;
        });
        return (
          <ChartCard key={idx} title={component.title}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false}
                  tick={{ fontSize: 9, fill: '#475569' }} angle={-45} textAnchor="end" height={50} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} width={35} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {series.map((s, i) => (
                  <Bar key={i} dataKey={s.name} stackId="stack" fill={CHART_COLORS[i % CHART_COLORS.length]} radius={i === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        );
      }

      case 'pie_chart':
      case 'donut_chart': {
        const chartData = (component.data || []).map(d => ({ name: d.label, value: d.value }));
        const isDonut = component.type === 'donut_chart';
        return (
          <ChartCard key={idx} title={component.title}>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={chartData} cx="50%" cy="50%" innerRadius={isDonut ? 40 : 0} outerRadius={65}
                  paddingAngle={2} dataKey="value" isAnimationActive={false}
                  label={false} labelLine={false}>
                  {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ padding: '4px 8px' }}>
              {chartData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 0', borderBottom: i < chartData.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: '#475569' }}>{d.name}</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{d.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </ChartCard>
        );
      }

      case 'leaderboard': {
        const items = component.data || [];
        return (
          <div key={idx} className="layout-leaderboard">
            <div className="leaderboard-title">{component.title}</div>
            {items.map((item, i) => (
              <div key={i} className="leaderboard-row">
                <span className={`leaderboard-rank ${i < 3 ? 'top-' + (i + 1) : ''}`}>{i + 1}</span>
                <span className="leaderboard-label">{item.label}</span>
                <span className="leaderboard-value">{item.value}</span>
              </div>
            ))}
          </div>
        );
      }

      case 'progress_bar': {
        const pct = Math.min(100, Math.round((component.value / (component.max || 100)) * 100));
        return (
          <div key={idx} className="layout-progress">
            <div className="progress-header">
              <span className="progress-title">{component.title}</span>
              <span className="progress-pct">{pct}%</span>
            </div>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${pct}%`, background: pct > 70 ? '#22c55e' : pct > 40 ? '#f59e0b' : '#ef4444' }} />
            </div>
          </div>
        );
      }

      case 'gauge_chart': {
        const min = component.min || 0;
        const max = component.max || 100;
        const val = component.value;
        const pct = Math.min(100, Math.round(((val - min) / (max - min)) * 100));
        const gaugeColor = pct > 70 ? '#22c55e' : pct > 40 ? '#f59e0b' : '#ef4444';
        return (
          <div key={idx} className="layout-gauge">
            <div className="gauge-title">{component.title}</div>
            <div className="gauge-visual">
              <svg viewBox="0 0 120 70" className="gauge-svg">
                <path d="M10 65 A50 50 0 0 1 110 65" fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
                <path d="M10 65 A50 50 0 0 1 110 65" fill="none" stroke={gaugeColor} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${pct * 1.57} 157`} />
              </svg>
              <div className="gauge-value" style={{ color: gaugeColor }}>{val}</div>
            </div>
            <div className="gauge-range">
              <span>{min}</span><span>{max}</span>
            </div>
          </div>
        );
      }

      case 'metric_grid': {
        const metrics = component.metrics || [];
        return (
          <div key={idx} className="layout-metric-grid">
            {metrics.map((m, i) => (
              <div key={i} className="metric-card">
                <div className="metric-title">{m.title}</div>
                <div className="metric-value">{m.value}</div>
              </div>
            ))}
          </div>
        );
      }

      case 'status_grid': {
        const items = component.data || [];
        const statusColors = { healthy: '#22c55e', error: '#ef4444', warning: '#f59e0b', offline: '#94a3b8', alert: '#f97316' };
        const statusIcons = { healthy: '✓', error: '✗', warning: '!', offline: '—', alert: '⚠' };
        return (
          <div key={idx} className="layout-status-grid">
            <div className="status-grid-title">{component.title}</div>
            <div className="status-grid-items">
              {items.map((item, i) => {
                const color = statusColors[item.status] || '#94a3b8';
                return (
                  <div key={i} className="status-item" style={{ borderLeft: `3px solid ${color}` }}>
                    <span className="status-icon" style={{ background: `${color}18`, color }}>
                      {statusIcons[item.status] || '?'}
                    </span>
                    <span className="status-label">{item.label}</span>
                    <span className="status-badge" style={{ background: `${color}15`, color }}>
                      {item.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case 'heatmap': {
        const data = component.data || [];
        const xs = [...new Set(data.map(d => d.x))];
        const ys = [...new Set(data.map(d => d.y))];
        const maxVal = Math.max(...data.map(d => d.value), 1);
        const getVal = (x, y) => { const m = data.find(d => d.x === x && d.y === y); return m ? m.value : 0; };
        return (
          <div key={idx} className="layout-heatmap">
            <div className="heatmap-title">{component.title}</div>
            <div className="heatmap-scroll">
              <table className="heatmap-table">
                <thead>
                  <tr><th></th>{xs.map((x, i) => <th key={i}>{x}</th>)}</tr>
                </thead>
                <tbody>
                  {ys.map((y, yi) => (
                    <tr key={yi}>
                      <td className="heatmap-row-label">{y}</td>
                      {xs.map((x, xi) => {
                        const v = getVal(x, y);
                        const intensity = v / maxVal;
                        return (
                          <td key={xi} className="heatmap-cell"
                            style={{ background: `rgba(102,126,234,${0.1 + intensity * 0.8})`, color: intensity > 0.5 ? 'white' : '#475569' }}
                            title={`${x}, ${y}: ${v}`}>
                            {v > 0 ? v : ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'table': {
        const columns = component.columns || [];
        const rows = component.rows || [];

        // Export function for this specific table
        const handleExportTable = () => {
          if (!rows || rows.length === 0) {
            alert(t('chatbot.no_data_to_export') || 'No data to export');
            return;
          }

          // Prepare data for export with proper formatting
          const exportData = rows.map(row => {
            const formattedRow = {};
            columns.forEach(col => {
              formattedRow[col] = row[col] != null ? String(row[col]) : '—';
            });
            return formattedRow;
          });

          // Create worksheet and workbook
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();

          // Auto-size columns
          const colWidths = columns.map(col => {
            const maxLength = Math.max(
              col.length,
              ...exportData.map(row => String(row[col] || '').length)
            );
            return { wch: Math.min(maxLength + 2, 50) }; // Max width 50
          });
          worksheet['!cols'] = colWidths;

          // Truncate sheet name to 31 characters (Excel limit)
          const sheetName = (component.title || 'Table').substring(0, 31);
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

          // Generate filename with timestamp
          const timestamp = new Date().toISOString().slice(0, 10);
          const filename = `${(component.title || 'Table').replace(/\s+/g, '_')}_${timestamp}.xlsx`;

          XLSX.writeFile(workbook, filename);
        };

        return (
          <div key={idx} className="layout-table-card">
            <div className="table-card-header">
              <div className="table-title">{component.title}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div className="table-count">{rows.length} records</div>
                <button
                  onClick={handleExportTable}
                  className="table-export-btn"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#667eea',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                    e.currentTarget.style.borderColor = '#667eea';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                  }}
                  title={t('chatbot.export_to_excel') || 'Export to Excel'}
                >
                  <Download size={14} />
                  {/* <span>{t('chatbot.export') || 'Export'}</span> */}
                </button>
              </div>
            </div>
            <div className="table-scroll">
              <table className="chat-table">
                <thead>
                  <tr>{columns.map((col, ci) => <th key={ci}>{col}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri}>
                      {columns.map((col, ci) => (
                        <td key={ci}>{row[col] != null ? String(row[col]) : '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'text': {
        return (
          <div key={idx} className="layout-text-block">
            {component.title && <div className="text-block-title">{component.title}</div>}
            <div
              className="text-block-content"
              dangerouslySetInnerHTML={{ __html: parseBotText(component.content || component.text || '') }}
            />
          </div>
        );
      }

      case 'timeline': {
        const events = component.data || component.events || [];
        return (
          <div key={idx} className="layout-timeline">
            {component.title && <div className="timeline-title">{component.title}</div>}
            <div className="timeline-track">
              {events.map((event, i) => {
                const dotColor = event.color || (event.status === 'error' ? '#ef4444' : event.status === 'warning' ? '#f59e0b' : event.status === 'success' ? '#22c55e' : '#667eea');
                return (
                  <div key={i} className="timeline-event">
                    <div className="timeline-line-container">
                      <div className="timeline-dot" style={{ background: dotColor }} />
                      {i < events.length - 1 && <div className="timeline-connector" />}
                    </div>
                    <div className="timeline-event-content">
                      <div className="timeline-event-header">
                        <span className="timeline-event-label">{event.label || event.title}</span>
                        {event.time && <span className="timeline-event-time">{event.time}</span>}
                      </div>
                      {event.description && <div className="timeline-event-desc">{event.description}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case 'comparison_card': {
        const items = component.items || component.data || [];
        return (
          <div key={idx} className="layout-comparison">
            {component.title && <div className="comparison-title">{component.title}</div>}
            <div className="comparison-items">
              {items.map((item, i) => (
                <div key={i} className="comparison-item">
                  <div className="comparison-item-label">{item.label}</div>
                  <div className="comparison-item-value">{item.value}</div>
                  {item.change && (
                    <div className={`comparison-item-change ${item.change > 0 ? 'positive' : item.change < 0 ? 'negative' : 'neutral'}`}>
                      {item.change > 0 ? '↑' : item.change < 0 ? '↓' : '→'} {Math.abs(item.change)}{item.unit || '%'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'alert_card': {
        const severity = component.severity || component.level || 'info';
        const alertConfig = {
          info: { icon: 'ℹ️', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
          success: { icon: '✅', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          warning: { icon: '⚠️', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
          error: { icon: '🚨', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
        };
        const cfg = alertConfig[severity] || alertConfig.info;
        return (
          <div key={idx} className="layout-alert-card" style={{ background: cfg.bg, borderColor: cfg.border }}>
            <div className="alert-card-icon">{cfg.icon}</div>
            <div className="alert-card-body">
              {component.title && <div className="alert-card-title" style={{ color: cfg.color }}>{component.title}</div>}
              <div className="alert-card-message">{component.message || component.content || component.text || ''}</div>
            </div>
          </div>
        );
      }

      case 'stat_row': {
        const stats = component.stats || component.data || [];
        return (
          <div key={idx} className="layout-stat-row">
            {component.title && <div className="stat-row-title">{component.title}</div>}
            <div className="stat-row-items">
              {stats.map((stat, i) => (
                <div key={i} className="stat-row-item">
                  <div className="stat-row-label">{stat.label}</div>
                  <div className="stat-row-value">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'image': {
        const src = component.url || component.src || '';
        return (
          <div key={idx} className="layout-image">
            {component.title && <div className="image-title">{component.title}</div>}
            {src && (
              <img
                className="image-content"
                src={src}
                alt={component.alt || component.title || 'Image'}
                loading="lazy"
              />
            )}
            {component.caption && <div className="image-caption">{component.caption}</div>}
          </div>
        );
      }

      default:
        return null;
    }
  };

  // ─── Render a structured bot message ───
  const renderBotMessage = (data) => {
    if (!data) return null;

    // Keys for parent menus that have sub-items (non-clickable headers)
    const parentOnlyKeys = ['chatbot.menu_view', 'chatbot.menu_manage', 'chatbot.menu_maintenance'];
    const isParentOnly = (name) => {
      const key = reverseMenuMap[name];
      return key && parentOnlyKeys.includes(key);
    };

    return (
      <div className="bot-response-container">
        {/* Greeting */}
        {data.greeting && (
          <div className="greeting">{translateLabel(data.greeting, 'text')}</div>
        )}

        {data.Descritions && (
          <div className="Descritions-container">
            {translateLabel(data.Descritions, 'text')}
          </div>
        )}
        {/* Role Badge */}
        {data.role && (
          <span className="role-badge">{data.role}</span>
        )}

        {/* Message - displayed right after role */}
        {data.message && (
          <div
            className="bot-message-text"
            dangerouslySetInnerHTML={{ __html: parseBotText(translateLabel(data.message, 'text')) }}
          />
        )}

        {/* Menu */}
        {data.menu && Array.isArray(data.menu) && (
          <div className="menu-container">
            {data.menu.map((item, idx) => (
              <div key={idx} className="menu-item1">
                <div
                  className="menu-header"
                  style={{
                    cursor: isParentOnly(item.name) ? 'default' : 'pointer'
                  }}
                  onClick={() => {
                    if (!isParentOnly(item.name)) {
                      askAboutTopic(item.name);
                    }
                  }}
                >
                  <span>{translateLabel(item.name, 'menu')}</span>
                </div>
                {item.items && Array.isArray(item.items) && (
                  <div className="menu-subitems">
                    {item.items.map((subitem, subIdx) => (
                      <div
                        key={subIdx}
                        className="menu-subitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          askAboutTopic(subitem);
                        }}
                      >
                        • {translateLabel(subitem, 'menu')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Structured Response (from workflow: summary + layout + suggestions) */}
        {data.structured && (() => {
          const sr = data.structured;
          return (
            <div className="structured-response">
              {/* Summary */}
              {sr.summary && (
                <div
                  className="bot-message-text"
                  dangerouslySetInnerHTML={{ __html: parseBotText(translateLabel(sr.summary, 'text')) }}
                />
              )}

              {/* Layout Components */}
              {sr.layout && Array.isArray(sr.layout) && (
                <div className="layout-components">
                  {sr.layout.map((comp, idx) => renderLayoutComponent(comp, idx))}
                </div>
              )}

              {/* Suggestions */}
              {sr.suggestions && Array.isArray(sr.suggestions) && sr.suggestions.length > 0 && (
                <div className="suggestions-container">
                  <div className="suggestions-label">💡 {t('chatbot.suggestions', 'Suggestions:')}</div>
                  <div className="suggestions-chips">
                    {sr.suggestions.map((s, idx) => (
                      <button
                        key={idx}
                        className="suggestion-chip"
                        onClick={() => sendMessage(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Legacy PDF Download (backward compat) */}
        {data.pdf_download && (() => {
          const pdfData = data.pdf_download;
          const dataUrl = pdfData.url || pdfData.pdf_url || pdfData.data;
          let downloadHref = null;
          let downloadName = pdfData.filename || 'report.pdf';

          if (dataUrl) {
            downloadHref = createBlobUrl(dataUrl, 'application/pdf');
          }

          return (
            <div className="bot-message-text">0
              {pdfData.text && (
                <div dangerouslySetInnerHTML={{ __html: parseBotText(pdfData.text) }} />
              )}
              {downloadHref ? (
                <a
                  href={downloadHref}
                  download={downloadName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pdf-download-btn"
                >
                  ⬇️ {t('chatbot.download_pdf_report', 'Download PDF Report')}
                </a>
              ) : (
                <div style={{ color: 'red' }}>({t('chatbot.error_generating_link', 'Error generating download link')})</div>
              )}
            </div>
          );
        })()}

        {/* Questions */}
        {data.questions && Array.isArray(data.questions) && (
          <div className="question-container">
            <div className="question-title">
              📚 {t('chatbot.questions_about', 'Questions about {{topic}}:', { topic: translateLabel(data.topic || currentTopic, 'menu') })}
            </div>
            {data.questions.map((question, idx) => (
              <button
                key={idx}
                className="question-button"
                onClick={() => selectIntent(question.id, question.text)}
              >
                {question.text}
              </button>
            ))}
          </div>
        )}

        {/* Text response (for answers from AI agent) */}
        {data.text && !data.questions && !data.structured && (
          <div
            className="bot-message-text"
            dangerouslySetInnerHTML={{ __html: parseBotText(typeof data.text === 'string' ? translateLabel(data.text, 'text') : JSON.stringify(data.text)) }}
          />
        )}

        {/* Error */}
        {data.error && (
          <div className="error-message">
            {/* <strong>{t('chatbot.error_title')}</strong>  */}
            {data.error}
          </div>
        )}

        {/* Back Navigation Buttons - show on all responses except the main menu */}
        {(!data.menu || data.questions) && (
          <div className="back-nav-buttons">
            {currentTopic && !data.questions && !data.isTyped && (
              <button
                className="back-nav-btn back-topic-btn"
                onClick={() => askAboutTopic(currentTopic)}
              >
                ← {t('chatbot.back_to_topic', 'Back to {{topic}}', { topic: translateLabel(currentTopic, 'menu') })}
              </button>
            )}
            <button
              className="back-nav-btn back-main-btn"
              onClick={goToMainMenu}
            >
              🏠 {t('chatbot.main_menu', 'Main Menu')}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Memoize rendered messages so typing in input doesn't re-render charts.
  // Attaches a ref to the last bot message so we can scrollIntoView on it.
  const renderedMessages = useMemo(() => {
    // Find the index of the last bot message for ref attachment
    let lastBotIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        lastBotIdx = i;
        break;
      }
    }

    return messages.map((msg, idx) => (
      <div
        key={msg.id}
        ref={idx === lastBotIdx ? lastBotMessageRef : undefined}
        className={`message ${msg.role === 'user' ? 'user' : 'bot'}`}
      >
        <div className="message-bubble">
          {msg.role === 'user' ? (
            <span>{msg.content}</span>
          ) : (
            msg.data ? renderBotMessage(msg.data) : (
              <div
                className="bot-message-text"
                dangerouslySetInnerHTML={{ __html: parseBotText(msg.content || '') }}
              />
            )
          )}
        </div>
      </div>
    ));
  }, [messages, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={onOpen}
          className="chat-fab"
          aria-label={t('chatbot.open_chat', 'Open chat')}
        >
          <div className="chat-fab-lottie">
            <Lottie 
              animationData={chatbotAnimation} 
              loop={true}
              autoplay={true}
              style={{ width: '40px', height: '40px' }}
            />
            {/* <img
              src={chatbotAnimation}
              alt="Chatbot"
              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
            /> */}
          </div>
        </button>
      )}

      {/* Chat Interface */}
      <div
        ref={chatContainerRef}
        className={`chat-container ${isOpen ? 'open' : ''} ${isDragging ? 'dragging' : ''} ${isPositionResetting ? 'position-resetting' : ''}`}
        style={{
          right: `${chatPosition.right}px`,
          bottom: `${chatPosition.bottom}px`
        }}
      >
        {/* Header */}
        <div
          className="chat-header"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >

          {/* <div className="chat-header-content" > */}
          <img src={logoIcon} alt={t('chatbot.logo_alt', 'Vending Machine Assistant')} className="chat-logo" />
          <h1 className='Naf_Assistant'>{t('chatbot.title', 'NAF Assistant')}</h1>
          {/* </div> */}

          <button
            onClick={handleReset}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="chat-reset-btn"
            title={t('chatbot.reset_chat', 'Reset Chat')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-rotate-ccw-icon lucide-rotate-ccw"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /></svg>
          </button>
          <button
            onClick={() => {
              setIsPositionResetting(true);
              setChatPosition({ right: 20, bottom: 20 });
              setTimeout(() => {
                onClose();
                setIsPositionResetting(false);
              }, 400);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="chat-close-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-minus-icon lucide-minus"><path d="M5 12h14" /></svg>
          </button>
        </div>

        {/* Messages Area */}
        <div className="chat-messages" ref={messagesContainerRef}>
          {renderedMessages}

          {isTyping && (
            <div className="message bot">
              <div className="typing-indicator-wrapper">
                <div className="typing-indicator">
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                </div>
                {getWaitMessage(elapsedTime) && (
                  <div className="typing-wait-message">
                    {getWaitMessage(elapsedTime)}
                  </div>
                )}

              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to Bottom Button - positioned relative to chat-container */}
        <div className="scroll-button-wrapper">
          {showScrollButton && (
            <button
              onClick={scrollToBottom}
              className="scroll-to-bottom-btn"
              title="Scroll to bottom"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          )}
        </div>

        {/* Input Area */}
        <div className="chat-input-container">
          <div className="chat-input-wrapper">
            {isTyping ? (
              <>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('chatbot.processing', 'Processing your request...')}
                  className="chat-input"
                  disabled={true}
                />
                <button
                  onClick={cancelRequest}
                  disabled={isStopping}
                  className={`cancel-button ${isStopping ? 'stopping' : ''}`}
                  title={isStopping ? t('chatbot.stopping', 'Stopping...') : t('chatbot.cancel_request', 'Cancel request')}
                >
                  {isStopping ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinner">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    </svg>
                  )}
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t('chatbot.type_message', 'Type your message...')}
                  className="chat-input"
                  disabled={isTyping}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || isTyping}
                  className="send-button"
                >
                  ➤
                </button>
              </>
            )}
          </div>
        </div>

        {/* Date Picker Overlay */}
        {isDatePickerOpen && (
          <div className="datepicker-overlay">
            <div className="datepicker-modal">
              <div className="datepicker-header">
                <h3>{t('chatbot.select_date_range', 'Select Date Range')}</h3>
                <button onClick={() => setIsDatePickerOpen(false)} className="close-picker-btn">
                  <X size={18} />
                </button>
              </div>
              <div className="datepicker-body">
                {/* Monthly Quick Select */}
                {activePickerId === 'datepicker:monthly' && (
                  <div className="month-quick-select">
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const now = new Date();
                        setDateRange([{
                          startDate: new Date(now.getFullYear(), now.getMonth(), 1),
                          endDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
                          key: 'selection'
                        }]);
                      }}
                    >
                      {t('chatbot.this_month', 'This Month')}
                    </button>
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const now = new Date();
                        setDateRange([{
                          startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
                          endDate: new Date(now.getFullYear(), now.getMonth(), 0),
                          key: 'selection'
                        }]);
                      }}
                    >
                      {t('chatbot.last_month', 'Last Month')}
                    </button>
                  </div>
                )}

                {/* Custom Range / Transactions Quick Select */}
                {(activePickerId === 'datepicker:custom' || activePickerId === 'datepicker:transactions') && (
                  <div className="month-quick-select">
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const end = new Date();
                        const start = new Date();
                        start.setDate(start.getDate() - 7);
                        setDateRange([{ startDate: start, endDate: end, key: 'selection' }]);
                      }}
                    >
                      {t('chatbot.last_7_days', 'Last 7 Days')}
                    </button>
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const end = new Date();
                        const start = new Date();
                        start.setDate(start.getDate() - 30);
                        setDateRange([{ startDate: start, endDate: end, key: 'selection' }]);
                      }}
                    >
                      {t('chatbot.last_30_days', 'Last 30 Days')}
                    </button>
                  </div>
                )}

                {/* Weekly Quick Select */}
                {activePickerId === 'datepicker:weekly' && (
                  <div className="month-quick-select">
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const now = new Date();
                        const day = now.getDay() || 7; // Get current day of week (1=Mon, ..., 7=Sun)
                        const start = new Date(now);
                        start.setDate(now.getDate() - day + 1);
                        const end = new Date(start);
                        end.setDate(start.getDate() + 6);
                        setDateRange([{ startDate: start, endDate: end, key: 'selection' }]);
                      }}
                    >
                      {t('chatbot.this_week', 'This Week')}
                    </button>
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const now = new Date();
                        const day = now.getDay() || 7;
                        const start = new Date(now);
                        start.setDate(now.getDate() - day - 6);
                        const end = new Date(start);
                        end.setDate(start.getDate() + 6);
                        setDateRange([{ startDate: start, endDate: end, key: 'selection' }]);
                      }}
                    >
                      {t('chatbot.last_week', 'Last Week')}
                    </button>
                  </div>
                )}

                {/* Today/Daily Quick Select */}
                {activePickerId === 'datepicker:today' && (
                  <div className="month-quick-select">
                    <button
                      className="quick-month-btn"
                      onClick={() => {
                        const today = new Date();
                        setDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
                      }}
                    >
                      {t('chatbot.today', 'Today')}
                    </button>
                  </div>
                )}

                {/* Tax Month Picker (No calendar, only months/years) */}
                {activePickerId === 'datepicker:tax' && (
                  <div className="tax-picker-container">
                    <div className="year-selector">
                      <button onClick={() => setTaxYear(prev => prev - 1)} disabled={taxYear <= 2025}>&lt;</button>
                      <span className="current-year">{taxYear}</span>
                      <button onClick={() => setTaxYear(prev => prev + 1)}>&gt;</button>
                    </div>
                    <div className="months-grid">
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
                        <button
                          key={month}
                          className={`month-btn ${dateRange[0].startDate.getMonth() === index && dateRange[0].startDate.getFullYear() === taxYear ? 'active' : ''}`}
                          onClick={() => {
                            const start = new Date(taxYear, index, 1);
                            const end = new Date(taxYear, index + 1, 0);
                            setDateRange([{ startDate: start, endDate: end, key: 'selection' }]);
                          }}
                        >
                          {month}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {activePickerId !== 'datepicker:tax' && (
                  <DateRange
                    editableDateInputs={true}
                    onChange={item => setDateRange([item.selection])}
                    moveRangeOnFirstSelection={false}
                    ranges={dateRange}
                    rangeColors={['#667eea']}
                  />
                )}
              </div>
              <div className="datepicker-footer">
                <button onClick={() => setIsDatePickerOpen(false)} className="picker-cancel-btn">
                  {t('chatbot.cancel', 'Cancel')}
                </button>
                <button onClick={handleDateSelectionSubmit} className="picker-apply-btn">
                  {t('chatbot.apply', 'Apply Selection')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};
